/* eslint-disable no-undefined */
/* eslint-disable max-statements */
/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { useNavigate, useParams } from "react-router-dom";
import React, { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { FormikHelpers } from "formik";
import { Box, Button, TextField, Typography } from "@mui/material";
import { getAllPropertiesForSubscriptions, getInterestListsForSubscriptions } from "types/gql-types";
import logger from "services/logger";
import { useSnackbar } from "components/haven-design/snackbar";
import { LoadingSpinner, ServerError } from "components/graphql-views";
import { AdminHeading } from "../shared/admin-form";
import SubscriptionForm, {
  SubscriptionForm as SubscriptionFormType,
} from "./form";
import {
  UPDATE_SUBSCRIPTION,
  GET_SUBSCRIPTION_BY_ID,
  GET_PROPERTIES_FOR_SUBSCRIPTIONS,
  GET_INTEREST_LISTS_FOR_SUBSCRIPTIONS,
  GET_PLANS_FOR_SUBSCRIPTIONS,
  GET_PRICING_FOR_SUBSCRIPTIONS,
  GET_FREQUENCIES,
  SET_SUBSCRIPTION_INVOICED_ID,
} from "./queries";
import {
  applySubscriptionGraphqlFieldErrors,
  formatSubscriptionFrequencyOptions,
  formatSubscriptionPlanOptions,
  formatSubscriptionPricingOptions,
  interestListLabel,
  propertyLabel,
  subscriptionFormDateFromString,
  subscriptionFormDateToString,
  withSubscriptionSelectOptionIfMissing,
} from "./subscription-utils";

const UpdateSubscription: React.FC = () => {
  const { subscriptionId } = useParams<{ subscriptionId: string }>();
  const navigate = useNavigate();
  const { openSnackbar } = useSnackbar();
  const [sending, setSending] = useState(false);
  const [updateSubscription] = useMutation(UPDATE_SUBSCRIPTION, {
    refetchQueries: ["subscriptionsAll"],
  });
  const [setInvoicedId] = useMutation(SET_SUBSCRIPTION_INVOICED_ID, {
    refetchQueries: ["subscriptionById", "subscriptionsAll"],
  });
  const [invoicedIdValue, setInvoicedIdValue] = useState("");
  const [savingInvoicedId, setSavingInvoicedId] = useState(false);

  const {
    loading,
    error,
    data: subscriptionData,
  } = useQuery(GET_SUBSCRIPTION_BY_ID, {
    variables: { id: subscriptionId },
    skip: !subscriptionId,
  });

  const { data: propertiesData } = useQuery<getAllPropertiesForSubscriptions>(
    GET_PROPERTIES_FOR_SUBSCRIPTIONS,
    {
      variables: { includeArchived: false },
      fetchPolicy: "network-only",
    },
  );
  const { data: interestListsData } = useQuery<getInterestListsForSubscriptions>(
    GET_INTEREST_LISTS_FOR_SUBSCRIPTIONS,
    {
      variables: { includeArchived: false },
      fetchPolicy: "network-only",
    },
  );
  const { data: plansData } = useQuery(GET_PLANS_FOR_SUBSCRIPTIONS);
  const { data: pricingData } = useQuery(GET_PRICING_FOR_SUBSCRIPTIONS);
  const { data: frequenciesData } = useQuery(GET_FREQUENCIES);

  React.useEffect(() => {
    const current = subscriptionData?.subscriptionById?.invoicedId;
    if (current) setInvoicedIdValue(current);
  }, [subscriptionData?.subscriptionById?.invoicedId]);

  const handleSaveInvoicedId = () => {
    if (!subscriptionId) return;
    setSavingInvoicedId(true);
    // The server verifies the id against Invoiced; a bad id errors and saves nothing.
    setInvoicedId({ variables: { id: subscriptionId, invoicedId: invoicedIdValue } })
      .then(() => openSnackbar({ message: "Invoiced ID verified and saved", status: "success" }))
      .catch((err) => {
        logger.sendError(err);
        openSnackbar({
          message: err?.graphQLErrors?.[0]?.message ?? err?.message ?? "Failed to save Invoiced ID",
          status: "error",
        });
      })
      .finally(() => setSavingInvoicedId(false));
  };

  const submitForm = (
    formData: SubscriptionFormType,
    {
      setErrors,
      setTouched,
    }: Pick<FormikHelpers<SubscriptionFormType>, "setErrors" | "setTouched">,
  ) => {
    if (!subscriptionId) {
      return Promise.resolve();
    }

    setSending(true);

    const cleanedData = {
      propertyId: formData.propertyId || undefined,
      interestListId: formData.interestListId || undefined,
      planId: formData.planId || undefined,
      pricingId: formData.pricingId || undefined,
      billingFrequencyId: formData.billingFrequencyId ?? undefined,
      startDate: subscriptionFormDateToString(formData.startDate ?? null),
      endDate: subscriptionFormDateToString(formData.endDate ?? null),
      renewalDate: subscriptionFormDateToString(formData.renewalDate ?? null),
    };

    return updateSubscription({
      variables: { id: subscriptionId, data: cleanedData },
      refetchQueries: ["subscriptionsAll"],
    })
      .then(() => {
        openSnackbar({
          message: "Subscription updated successfully",
          status: "success",
        });
        navigate("/dash/admin/subscriptions");
      })
      .catch((err) => {
        logger.sendError(err);
        const message =
          err?.graphQLErrors?.[0]?.message ??
          err?.message ??
          "Failed to update subscription";
        if (!applySubscriptionGraphqlFieldErrors(message, { setErrors, setTouched })) {
          openSnackbar({
            message,
            status: "error",
          });
        }
      })
      .finally(() => {
        setSending(false);
      });
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ServerError desc={error.message} />;
  if (!subscriptionData?.subscriptionById) return <div>Subscription not found</div>;

  const sub = subscriptionData.subscriptionById;
  const pil = sub.propertyInterestList;

  const properties = propertiesData?.properties ?? [];
  const interestLists = interestListsData?.interestLists ?? [];
  const plans = plansData?.plansAll ?? [];
  const pricingList = pricingData?.pricingAll ?? [];
  const frequencies = frequenciesData?.frequenciesAll ?? [];

  const propertyOptions = withSubscriptionSelectOptionIfMissing(
    properties.map(
      (p) => ({
        value: p.id,
        label: propertyLabel(p),
      }),
    ),
    pil?.propertyId ?? pil?.property?.id,
    pil?.property ? propertyLabel(pil.property) : undefined,
  );

  const interestListOptions = withSubscriptionSelectOptionIfMissing(
    interestLists.map(
      (il) => ({
        value: il.id,
        label: interestListLabel(il),
      }),
    ),
    pil?.interestListId ?? pil?.interestList?.id,
    pil?.interestList ? interestListLabel(pil.interestList) : undefined,
  );

  const planOptions = formatSubscriptionPlanOptions(plans);

  const pricingOptions = formatSubscriptionPricingOptions(pricingList);

  const frequencyOptions = formatSubscriptionFrequencyOptions(frequencies);

  const initialData: SubscriptionFormType = {
    propertyId: pil?.propertyId ?? pil?.property?.id ?? "",
    interestListId: pil?.interestListId ?? pil?.interestList?.id ?? "",
    planId: sub.planId ?? sub.plan?.id ?? "",
    pricingId: sub.pricingId ?? sub.pricing?.id ?? "",
    billingFrequencyId:
      sub.billingFrequencyId ?? sub.billingFrequency?.id ?? undefined,
    startDate: subscriptionFormDateFromString(sub.startDate ?? null),
    endDate: subscriptionFormDateFromString(sub.endDate ?? null),
    renewalDate: subscriptionFormDateFromString(sub.renewalDate ?? null),
  };

  return (
    <div>
      <AdminHeading>Update Subscription</AdminHeading>
      {sub.propertyContract && (
        <Box sx={{ mb: 2 }} id="linked-contract">
          <Typography variant="subtitle2">Linked contract</Typography>
          <Typography variant="body2">
            {sub.propertyContract.propertyName ?? "Contract"} ({sub.propertyContract.status ?? "—"})
          </Typography>
        </Box>
      )}
      <Box sx={{ mb: 2, display: "flex", gap: 1, alignItems: "flex-start" }} id="invoiced-id-section">
        <TextField
          id="invoiced-id-input"
          label="Invoiced ID"
          size="small"
          value={invoicedIdValue}
          onChange={(e) => setInvoicedIdValue(e.target.value)}
          helperText="Verified against Invoiced before saving"
        />
        <Button
          id="save-invoiced-id"
          variant="contained"
          onClick={handleSaveInvoicedId}
          disabled={savingInvoicedId || !invoicedIdValue}
        >
          Save
        </Button>
      </Box>
      <SubscriptionForm
        formType="update"
        initialData={initialData}
        sending={sending}
        handleSubmit={submitForm}
        propertyOptions={propertyOptions}
        interestListOptions={interestListOptions}
        planOptions={planOptions}
        pricingOptions={pricingOptions}
        frequencyOptions={frequencyOptions}
      />
    </div>
  );
};

export default UpdateSubscription;
